import React, { createContext, useContext, useMemo } from 'react';
import { AhojDataContext, AhojDataContextType } from './AhojDataContext';
import { StructureEventBusContext, StructureEventBusContextType } from './StructureEventBusContext';
import { StructureStateContext, StructureStateContextType } from './StructureStateContext';

/**
 * Merged context type consumed by class-based table components via `static contextType`.
 * Combines the three separate contexts into a single object so that class components,
 * which cannot use multiple hooks, still have access to the full context API.
 *
 * Tricky: class components can only have ONE `contextType`, so this merged context is
 * the only way to give them access to the bus, state, and data simultaneously.
 */
export type AhojTableContextType = StructureEventBusContextType & StructureStateContextType & AhojDataContextType;

export const AhojTableContext = createContext<AhojTableContextType>({} as AhojTableContextType);

/**
 * Provider that merges `StructureEventBusContext`, `StructureStateContext`, and
 * `AhojDataContext` into a single `AhojTableContext` value using `useMemo`.
 * The memo dependency array spans all three contexts so the merged object is only
 * recomputed when at least one of the underlying contexts changes.
 */
export const AhojTableProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
   const bus = useContext(StructureEventBusContext);
   const state = useContext(StructureStateContext);
   const controls = useContext(AhojDataContext);
   const value = useMemo<AhojTableContextType>(() => ({ ...bus, ...state, ...controls }), [bus, state, controls]);
   return <AhojTableContext.Provider value={value}>{children}</AhojTableContext.Provider>;
};
